import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { trackRecommenderWidgetToggled } from "../analytics";
import { isCartRequiredForRecommendations } from "../selectors";
import type { RecommenderToggleResult } from "../types";

/**
 * `useRecommendationWidget` — the collapse/disable state machine for a COLLAPSIBLE surface.
 *
 * Split out from `useRecommendations` on purpose: a kiosk carousel that is always on screen wants
 * the data hook and none of this. A POS widget with a "Rista AI" tab wants both.
 *
 * The machine has two independent flags:
 *  - **disabled** — there is nothing to show. Driven by the data, not the operator.
 *  - **collapsed** — the operator (or the disable rule) has folded it away.
 *
 * They interact: the widget auto-opens the moment suggestions arrive and auto-folds when the last
 * one goes, so the operator never has to hunt for it, but a manual collapse still sticks until
 * the next response.
 *
 * ## Why refusals are RETURNED, not toasted
 *
 * Two taps are legitimately refused — opening an empty V1 widget, and opening one with no
 * suggestions. Core cannot show a toast (it must not import UI), and more importantly a kiosk
 * would not WANT a toast. So `toggleCollapsed()` returns `{ ok: false, reason, message }` and the
 * surface decides: the POS shows the message, a kiosk ignores it.
 */
export interface UseRecommendationWidgetOptions {
  /** Whether the flow currently has anything to render. */
  hasItems: boolean;
  /** Cart size, for the V1 "add an item first" refusal. */
  cartItemCount: number;
  /** Start folded. True on the POS, where the widget is a tab. */
  initiallyCollapsed?: boolean;
  /** Report manual open/close to GA. On by default. */
  trackToggle?: boolean;
}

export interface UseRecommendationWidgetResult {
  isCollapsed: boolean;
  /** Nothing to show — render the tab greyed, or not at all. */
  isDisabled: boolean;
  /** Toggle by hand. Returns why it refused, if it did. */
  toggleCollapsed: () => RecommenderToggleResult;
  /** Fold it away without a refusal check — e.g. leaving the screen. */
  collapse: () => void;
}

export const RECOMMENDER_MESSAGES: Record<string, string> = {
  EMPTY_CART: "To get recommendations, please add an item in the cart",
  NO_RECOMMENDATIONS: "No recommendation available",
};

export function useRecommendationWidget(
  options: UseRecommendationWidgetOptions,
): UseRecommendationWidgetResult {
  const { hasItems, cartItemCount, initiallyCollapsed = true, trackToggle = true } = options;

  const cartRequired = useSelector(isCartRequiredForRecommendations);
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);
  const [isDisabled, setIsDisabled] = useState(false);

  // Data drives the disabled flag; the disabled flag drives the fold. Suggestions arriving opens
  // the widget, the last one leaving folds it.
  useEffect(() => {
    if (hasItems && isDisabled) {
      setIsDisabled(false);
      setIsCollapsed(false);
    } else if (!hasItems && !isDisabled) {
      setIsDisabled(true);
      setIsCollapsed(true);
    }
  }, [hasItems, isDisabled]);

  const toggleCollapsed = useCallback((): RecommenderToggleResult => {
    if (cartRequired && isCollapsed && !cartItemCount) {
      return { ok: false, reason: "EMPTY_CART", message: RECOMMENDER_MESSAGES.EMPTY_CART };
    }
    if (isDisabled && isCollapsed) {
      return {
        ok: false,
        reason: "NO_RECOMMENDATIONS",
        message: RECOMMENDER_MESSAGES.NO_RECOMMENDATIONS,
      };
    }
    const isOpening = isCollapsed;
    setIsCollapsed(!isCollapsed);
    if (trackToggle) {
      trackRecommenderWidgetToggled(isOpening);
    }
    return { ok: true };
  }, [cartRequired, isCollapsed, cartItemCount, isDisabled, trackToggle]);

  const collapse = useCallback(() => setIsCollapsed(true), []);

  return { isCollapsed, isDisabled, toggleCollapsed, collapse };
}
